import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatMomentDateModule, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';

import { GridModule, GroupService } from '@syncfusion/ej2-angular-grids';
import {
  MagnificationService,
  NavigationService,
  PdfViewerModule,
  ThumbnailViewService,
  ToolbarService
} from '@syncfusion/ej2-angular-pdfviewer';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG
} from '@azure/msal-angular';
import { environment } from 'src/environments/environment';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RxReactiveFormsModule } from '@rxweb/reactive-form-validators';
import { MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from './auth/msal.utils';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BlockCopyPasteDirective } from './shared/block-copy-paste.directive';
import { NameMustMatchDirective } from './shared/name-must-match.directive';
import { NgxIndexedDBModule } from 'ngx-indexed-db';
import { NavMenuComponent } from './shared/nav-menu.component';

const MAT_MODULES = [
  MatButtonModule,
  MatCardModule,
  MatCheckboxModule,
  MatDatepickerModule,
  MatDialogModule,
  MatIconModule,
  MatInputModule,
  MatListModule,
  MatMenuModule,
  MatMomentDateModule,
  MatProgressSpinnerModule,
  MatRadioModule,
  MatSelectModule,
  MatSidenavModule,
  MatSlideToggleModule,
  MatSnackBarModule,
  MatSortModule,
  MatTableModule,
  MatTabsModule,
  MatToolbarModule
];

const SYNCFUSION_MODULES = [GridModule, PdfViewerModule];
const SYNCFUSION_PROVIDERS = [
  GroupService,
  MagnificationService,
  ThumbnailViewService,
  ToolbarService,
  NavigationService
];

@NgModule({
  bootstrap: [MsalRedirectComponent],
  imports: [
    BlockCopyPasteDirective,
    NameMustMatchDirective,
    NavMenuComponent,
    BrowserModule,
    BrowserAnimationsModule,
    ...MAT_MODULES,
    ...SYNCFUSION_MODULES,
    FormsModule,
    ReactiveFormsModule,
    MsalModule,
    RxReactiveFormsModule,
    LoggerModule.forRoot({
      level: environment.production ? NgxLoggerLevel.OFF : NgxLoggerLevel.LOG,
      serverLogLevel: NgxLoggerLevel.OFF,
      enableSourceMaps: !environment.production
    }),
    NgxIndexedDBModule.forRoot({
      name: 'AnnualDeclarationsDb',
      version: 1,
      objectStoresMeta: [
        {
          store: 'pdf_documents',
          storeConfig: { keyPath: 'id', autoIncrement: true },
          storeSchema: [
            { name: 'pdf_fileName', keypath: 'pdf_fileName', options: { unique: true } },
            { name: 'pdf_base64', keypath: 'pdf_base64', options: { unique: false } }
          ]
        }
      ]
    })
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    ...SYNCFUSION_PROVIDERS,
    provideHttpClient(withXhr(), withInterceptorsFromDi())
  ]
})
export class AppModule {}
